import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * "Who can do what" — the prototype's paragraph, "See every role" and "Open the audit log", every word
 * of it **derived from the capability module**, not typed here. Redrawn by plan-014 unit C3 (design
 * §27.15.1 `Settings` changed: "seven roles … Organization roles — Owner, Admin, Finance, Viewer — are
 * set separately" from the capability module).
 *
 * `packages/domain`'s `capability.ts` is the matrix (design §27.5.2, §27.5.3), and `workspaceRolesHolding`
 * / `accountRolesHolding` are the only sanctioned ways to ask it a question; the API's evaluator and the
 * database's predicates enforce the same answers. So the role names come from `MEMBER_ROLE` and
 * `ACCOUNT_ROLE`, "only Owners and Admins hear recordings or see caller details" is asked of
 * `recording.play` and `caller_detail.read`, and "See every role" opens both whole matrices — seven
 * workspace roles, four organization roles — with a tick wherever the module says a role holds the
 * capability. A screen that restated the ticks in JSX would be a fourth copy, and the fourth copy is
 * the one that is wrong.
 *
 * Earlier rounds (customer-portal review #64, #134, #194, #196) still hold: the expansion is a working
 * toggle, the audit log link is offered only to the roles its route admits (everyone else reads who can
 * open it), and the tables sit in a scroll box whose visually hidden labels cannot widen the page.
 */
import {
  ACCOUNT_CAPABILITY,
  ACCOUNT_ROLE,
  MEMBER_ROLE,
  WORKSPACE_CAPABILITY,
  accountRolesHolding,
  workspaceRolesHolding,
  type AccountCapability,
  type AccountRole,
  type MemberRole,
  type WorkspaceCapability,
} from "@wetalk/domain";
import { Icon, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";

import "./settings.css";

/** Plain-English for each workspace capability. The identifiers are the contract; these are the copy. */
export const WORKSPACE_CAPABILITY_COPY: Readonly<Record<WorkspaceCapability, string>> = {
  get "analytics.read"() { return portalText("core", "settings.see_analytics_spend_and_outcomes_dcc6cf"); },
  get "billing.read"() { return portalText("core", "settings.see_the_workspace_s_spend_and_usage_8f7cd2"); },
  get "conversation.read"() { return portalText("core", "account_admin.read_conversations_fdbc22"); },
  get "transcript.read"() { return portalText("core", "settings.read_transcripts_b7bf3d"); },
  get "caller_detail.read"() { return portalText("core", "settings.see_the_caller_s_number_or_address_301b1b"); },
  get "recording.play"() { return portalText("core", "settings.play_a_recording_ec4a74"); },
  get "voice_agent.write"() { return portalText("core", "settings.create_edit_publish_or_pause_an_agent_d5046f"); },
  get "agent_document.write"() { return portalText("core", "settings.upload_or_remove_the_documents_an_agent_answers_from_923b4c"); },
  get "channel_binding.write"() { return portalText("core", "settings.bind_a_number_a_sip_endpoint_or_a_web_widget_9707dc"); },
  get "webhook_endpoint.manage"() { return portalText("core", "settings.manage_webhook_destinations_57f179"); },
  get "campaign.operate"() { return portalText("core", "settings.start_pause_or_stop_an_outbound_campaign_0468a5"); },
  get "contact_list.manage"() { return portalText("core", "settings.manage_a_campaign_s_contact_lists_9d8681"); },
  get "export.conversation"() { return portalText("core", "settings.export_conversations_847c2c"); },
  get "export.campaign"() { return portalText("core", "settings.export_campaign_results_and_contacts_6a496f"); },
  get "do_not_call_entry.add"() { return portalText("core", "settings.add_a_number_to_the_do_not_call_list_e83eb0"); },
  get "do_not_call_entry.remove"() { return portalText("core", "settings.take_a_number_off_the_do_not_call_list_f1d31e"); },
  get "membership.read"() { return portalText("core", "settings.see_who_is_in_this_workspace_763fc7"); },
  get "membership.invite"() { return portalText("core", "account_admin.invite_someone_9768ea"); },
  get "membership.role_change"() { return portalText("core", "settings.change_someone_s_role_23959e"); },
  get "membership.remove"() { return portalText("core", "settings.remove_someone_16c35b"); },
  get "api_key.manage"() { return portalText("core", "settings.create_rotate_and_revoke_this_workspace_s_api_keys_912af9"); },
  get "api_key.integration_manage"() { return portalText("core", "settings.create_integration_keys_for_agents_and_conversations_74db6e"); },
  get "spend_cap.write"() { return portalText("core", "settings.set_the_workspace_s_spending_cap_70f3e1"); },
  get "resource_share.publish"() { return portalText("core", "settings.share_an_agent_or_a_list_with_another_workspace_acebcf"); },
  get "resource_share.accept"() { return portalText("core", "settings.accept_something_shared_with_this_workspace_6ae0d4"); },
  get "workspace.lifecycle"() { return portalText("core", "settings.archive_or_restore_this_workspace_335177"); },
  get "audit_entry.read"() { return portalText("core", "settings.read_the_audit_log_e26b54"); },
  get "compliance.operator_access"() { return portalText("core", "settings.let_wetalk_support_play_recordings_4510ba"); },
  get "person_data_request.create"() { return portalText("core", "settings.request_a_person_s_data_or_its_deletion_4a4c44"); },
  get "compliance.consent_line"() { return portalText("core", "settings.change_what_callers_are_told_before_a_recording_starts_cae924"); },
  get "customer_order.refund"() { return portalText("core", "settings.refund_an_order_s_payment_2c03a0"); },
};

/** Plain-English for each organization capability. */
export const ACCOUNT_CAPABILITY_COPY: Readonly<Record<AccountCapability, string>> = {
  get "account.profile.read"() { return portalText("core", "settings.see_the_organization_s_name_time_zone_and_language_738745"); },
  get "account.workspace_metadata.read"() { return portalText("core", "settings.see_which_workspaces_exist_never_their_content_c9922e"); },
  get "account.profile.write"() { return portalText("core", "settings.change_the_organization_s_details_c9cdc0"); },
  get "account.workspace.create"() { return portalText("core", "workspaces.create_a_workspace_e94c64"); },
  get "account.workspace.manage"() { return portalText("core", "settings.rename_archive_or_restore_a_workspace_d972a5"); },
  get "account.workspace_access.grant"() { return portalText("core", "settings.give_someone_access_to_a_workspace_with_a_reason_a339b6"); },
  get "account_membership.read"() { return portalText("core", "settings.see_the_organization_s_people_adbdda"); },
  get "account_membership.invite"() { return portalText("core", "settings.invite_someone_to_the_organization_c1a7ff"); },
  get "account_membership.role_change"() { return portalText("core", "settings.change_someone_s_organization_role_fb09ea"); },
  get "account_membership.remove"() { return portalText("core", "settings.remove_someone_from_the_organization_c98891"); },
  get "account.ownership.transfer"() { return portalText("core", "settings.hand_over_ownership_0af227"); },
  get "account.close"() { return portalText("core", "account_admin.close_the_organization_e79173"); },
  get "account.finance.read"() { return portalText("core", "account_admin.see_balances_invoices_and_statements_af6071"); },
  get "account.finance.manage"() { return portalText("core", "settings.pay_top_up_and_change_how_the_organization_pays_51f7cd"); },
  get "account.billing_delegate.manage"() { return portalText("core", "settings.let_someone_manage_a_workspace_s_own_balance_fcc131"); },
  get "account.security.read"() { return portalText("core", "settings.see_the_sign_in_policy_and_verified_domains_7d44af"); },
  get "account.sign_in_policy.write"() { return portalText("core", "settings.change_how_people_sign_in_0f57e3"); },
  get "account.audit.read"() { return portalText("core", "settings.read_the_organization_s_audit_log_72ba5d"); },
  get "account.audit.financial_read"() { return portalText("core", "settings.read_the_organization_s_financial_audit_entries_6eedcf"); },
  get "account.api_key.manage"() { return portalText("core", "settings.create_organization_keys_and_integration_keys_beeb70"); },
  get "account.resource_share.read"() { return portalText("core", "settings.see_what_is_shared_between_workspaces_11ae3e"); },
};

const MEMBER_ROLE_LABEL: Readonly<Record<MemberRole, string>> = {
  get owner() { return portalText("core", "account_admin.owner_89ff31"); },
  get admin() { return portalText("core", "account_admin.admin_4e7afe"); },
  get member() { return portalText("core", "account_admin.member_6853c9"); },
  get viewer() { return portalText("core", "account_admin.viewer_dbad9f"); },
  get developer() { return portalText("core", "account_admin.developer_7e08eb"); },
  get campaign_manager() { return portalText("core", "account_admin.campaign_manager_ce843f"); },
  get billing() { return portalText("core", "account_admin.billing_abaec4"); },
};

const ACCOUNT_ROLE_LABEL: Readonly<Record<AccountRole, string>> = {
  get owner() { return portalText("core", "account_admin.owner_89ff31"); },
  get admin() { return portalText("core", "account_admin.admin_4e7afe"); },
  get finance() { return portalText("core", "account_admin.finance_1b48d3"); },
  get viewer() { return portalText("core", "account_admin.viewer_dbad9f"); },
};

const MEMBER_ROLE_PLURAL: Readonly<Record<MemberRole, string>> = {
  get owner() { return portalText("core", "settings.owners_4eaec4"); },
  get admin() { return portalText("core", "settings.admins_ed6b52"); },
  get member() { return portalText("core", "settings.members_1cb449"); },
  get viewer() { return portalText("core", "settings.viewers_0ae629"); },
  get developer() { return portalText("core", "settings.developers_71348b"); },
  get campaign_manager() { return portalText("core", "settings.campaign_managers_6c8771"); },
  get billing() { return portalText("core", "settings.billing_members_af3ab6"); },
};

/** "A, B and C". */
function andList(named: readonly string[]): string {
  if (named.length <= 1) return named[0] ?? "";
  return portalText("core", "account_admin._and_4142c0", { value0: named.slice(0, -1).join(", "), value1: named.at(-1) ?? "" });
}

/** The workspace roles holding every capability in the list, strongest first. */
export function workspaceRolesHoldingAll(capability: readonly WorkspaceCapability[]): readonly MemberRole[] {
  return MEMBER_ROLE.filter((role) => capability.every((each) => workspaceRolesHolding(each).includes(role)));
}

/**
 * The prototype's paragraph, from the module: "This workspace uses seven roles: Owner, Admin, Member,
 * Viewer, Developer, Campaign manager and Billing. Only Owners and Admins hear recordings or see caller
 * details. Organization roles — Owner, Admin, Finance, Viewer — are set separately."
 */
export function whoCanDoWhatText(): string {
  const count = portalLocale() === "en" ? (["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"][MEMBER_ROLE.length] ?? String(MEMBER_ROLE.length)) : String(MEMBER_ROLE.length);
  const hearing = workspaceRolesHoldingAll(["recording.play", "caller_detail.read"]).map((one) => MEMBER_ROLE_PLURAL[one]);
  return (
    portalText("core", "settings.this_workspace_uses_roles_c29652", { value0: count, value1: andList(MEMBER_ROLE.map((one) => MEMBER_ROLE_LABEL[one])) }) +
    portalText("core", "settings.only_hear_recordings_or_see_caller_details_b6886a", { value0: andList(hearing) }) +
    portalText("core", "settings.organization_roles_are_set_separately_32915a", { value0: ACCOUNT_ROLE.map((one) => ACCOUNT_ROLE_LABEL[one]).join(", ") })
  );
}

function Held({ held }: { readonly held: boolean }): ReactNode {
  usePortalTranslation("core");
  return (
    <span className={held ? "c-matrix-held" : "c-matrix-withheld"}>
      <Icon glyph={held ? GLYPH.checkCircle : GLYPH.minus} size="md" />
      <span className="c-visually-hidden">{held ? portalText("core", "workspaces.yes_fb360f") : portalText("core", "workspaces.no_fd1286")}</span>
    </span>
  );
}

/** What a role's survey results download holds: one row per conversation, the totals only, or nothing (the api's rule). */
export function surveyDownloadOf(role: MemberRole): "rows" | "totals" | "none" {
  if (!workspaceRolesHolding("conversation.read").includes(role)) return "none";
  return workspaceRolesHolding("transcript.read").includes(role) ? "rows" : "totals";
}

export function PermissionMatrix(): ReactNode {
  usePortalTranslation("core");
  const [everyOpen, setEveryOpen] = useState(false);
  const auditRefusal = useMemberCan().whyNot("audit_entry.read");
  return (
    <div className="c-settings-matrix">
      <p className="c-settings-title">{portalText("core", "settings.who_can_do_what_0e60bd")}</p>
      <p className="c-settings-dek c-settings-dek-wide">{whoCanDoWhatText()}</p>

      <div className="c-settings-actions">
        <button
          type="button"
          className="btn btn-secondary"
          aria-expanded={everyOpen}
          onClick={() => {
            setEveryOpen((open) => !open);
          }}
        >
          {everyOpen ? portalText("core", "settings.hide_the_roles_3645a4") : portalText("core", "settings.see_every_role_00bc8d")}
        </button>
        {auditRefusal === null ? (
          <Link to="/audit" search={{ eventClass: null }} className="btn btn-ghost c-settings-link">{portalText("core", "settings.open_the_audit_log_a35e16")}</Link>
        ) : null}
      </div>
      {auditRefusal === null ? null : <p className="c-note">{auditRefusal}</p>}

      {everyOpen ? (
        <div className="c-settings-every">
          <div className="c-settings-scroll">
            <Table caption={portalText("core", "settings.every_workspace_capability_and_the_seven_roles_that_may_hold_it_10a715")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("core", "settings.in_this_workspace_e62eac")}</TableHeaderCell>
                  {MEMBER_ROLE.map((role) => (
                    <TableHeaderCell key={role}>{MEMBER_ROLE_LABEL[role]}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {WORKSPACE_CAPABILITY.map((capability) => (
                  <TableRow key={capability}>
                    <TableCell>{WORKSPACE_CAPABILITY_COPY[capability]}</TableCell>
                    {MEMBER_ROLE.map((role) => (
                      <TableCell key={role}>
                        <Held held={workspaceRolesHolding(capability).includes(role)} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Owner decision 2026-11-03: survey results download for every role that reads them; a role that does
                    not read transcripts (a Viewer) gets the totals only — the api's rule (routes/survey, design §16.7.2). */}
                <TableRow>
                  <TableCell>{portalText("core", "settings.download_survey_results_e512a2")}</TableCell>
                  {MEMBER_ROLE.map((role) => (
                    <TableCell key={role}>
                      {surveyDownloadOf(role) === "totals" ? (
                        <span className="c-matrix-held">{portalText("core", "settings.totals_only_160797")}</span>
                      ) : (
                        <Held held={surveyDownloadOf(role) === "rows"} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className="c-settings-scroll">
            <Table caption={portalText("core", "settings.every_organization_capability_and_the_four_organization_roles_86408a")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("core", "settings.in_the_organization_dedef9")}</TableHeaderCell>
                  {ACCOUNT_ROLE.map((role) => (
                    <TableHeaderCell key={role}>{ACCOUNT_ROLE_LABEL[role]}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ACCOUNT_CAPABILITY.map((capability) => (
                  <TableRow key={capability}>
                    <TableCell>{ACCOUNT_CAPABILITY_COPY[capability]}</TableCell>
                    {ACCOUNT_ROLE.map((role) => (
                      <TableCell key={role}>
                        <Held held={accountRolesHolding(capability).includes(role)} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
